/**
 * Detail Explorer — custom element for tab switching and FAQ accordion
 */
class DetailExplorerComponent extends HTMLElement {
  /** @type {AbortController | null} */
  #abortController = null;

  connectedCallback() {
    if (this.dataset.detailExplorerInitialized === 'true') {
      return;
    }

    this.dataset.detailExplorerInitialized = 'true';
    this.#abortController = new AbortController();
    const { signal } = this.#abortController;

    this.#initTabFocus();
    this.#bindTabs(signal);
    this.#bindFaqs(signal);
  }

  disconnectedCallback() {
    this.#abortController?.abort();
    this.#abortController = null;
    this.removeAttribute('data-detail-explorer-initialized');
  }

  #initTabFocus() {
    const tabBtns = this.querySelectorAll('.detail-explorer__tab-btn');

    for (const btn of tabBtns) {
      if (!(btn instanceof HTMLButtonElement)) continue;
      btn.setAttribute('tabindex', btn.classList.contains('active') ? '0' : '-1');
    }
  }

  /**
   * @param {AbortSignal} signal
   */
  #bindTabs(signal) {
    const tablist = this.querySelector('[role="tablist"]');
    if (!tablist || tablist.hasAttribute('data-de-tabs-bound')) {
      return;
    }

    tablist.setAttribute('data-de-tabs-bound', 'true');

    tablist.addEventListener(
      'click',
      (event) => {
        const btn = event.target instanceof Element
          ? event.target.closest('.detail-explorer__tab-btn')
          : null;

        if (!(btn instanceof HTMLButtonElement) || !tablist.contains(btn)) {
          return;
        }

        event.preventDefault();
        this.#activateTab(btn);
      },
      { signal }
    );

    tablist.addEventListener(
      'keydown',
      (event) => {
        if (!(event.target instanceof HTMLButtonElement)) return;
        if (!event.target.classList.contains('detail-explorer__tab-btn')) return;
        if (!tablist.contains(event.target)) return;

        const tabBtns = [...tablist.querySelectorAll('.detail-explorer__tab-btn')].filter(
          (el) => el instanceof HTMLButtonElement
        );
        const index = tabBtns.indexOf(event.target);
        if (index === -1) return;

        let nextIndex = index;

        if (event.key === 'ArrowRight' || event.key === 'ArrowDown') {
          nextIndex = (index + 1) % tabBtns.length;
        } else if (event.key === 'ArrowLeft' || event.key === 'ArrowUp') {
          nextIndex = (index - 1 + tabBtns.length) % tabBtns.length;
        } else if (event.key === 'Home') {
          nextIndex = 0;
        } else if (event.key === 'End') {
          nextIndex = tabBtns.length - 1;
        } else {
          return;
        }

        event.preventDefault();
        tabBtns[nextIndex].focus();
        this.#activateTab(tabBtns[nextIndex]);
      },
      { signal }
    );
  }

  /**
   * @param {string} paneId
   * @returns {HTMLElement | null}
   */
  #findPane(paneId) {
    for (const pane of this.querySelectorAll('.detail-explorer__tab-pane')) {
      if (pane instanceof HTMLElement && pane.id === paneId) {
        return pane;
      }
    }

    return null;
  }

  /**
   * @param {HTMLButtonElement} btn
   */
  #activateTab(btn) {
    const targetTab = btn.getAttribute('data-tab');
    if (!targetTab) return;

    const targetPane = this.#findPane(targetTab);
    if (!targetPane) return;

    const tabBtns = this.querySelectorAll('.detail-explorer__tab-btn');
    const tabPanes = this.querySelectorAll('.detail-explorer__tab-pane');

    for (const tabBtn of tabBtns) {
      tabBtn.classList.remove('active');
      tabBtn.setAttribute('aria-selected', 'false');
      tabBtn.setAttribute('tabindex', '-1');
    }

    btn.classList.add('active');
    btn.setAttribute('aria-selected', 'true');
    btn.setAttribute('tabindex', '0');

    for (const pane of tabPanes) {
      if (!(pane instanceof HTMLElement)) continue;
      pane.classList.remove('active');
      pane.setAttribute('aria-hidden', 'true');
      pane.setAttribute('hidden', '');
    }

    targetPane.classList.add('active');
    targetPane.setAttribute('aria-hidden', 'false');
    targetPane.removeAttribute('hidden');
  }

  /**
   * @param {AbortSignal} signal
   * @param {ParentNode} [scope]
   */
  #bindFaqs(signal, scope = this) {
    const faqQuestions = scope.querySelectorAll('.detail-explorer__faq-question:not([data-de-faq-bound])');

    for (const question of faqQuestions) {
      if (!(question instanceof HTMLButtonElement)) continue;

      question.setAttribute('data-de-faq-bound', 'true');
      question.addEventListener(
        'click',
        () => {
          const answer = question.nextElementSibling;
          if (!(answer instanceof HTMLElement)) return;

          const isExpanded = question.getAttribute('aria-expanded') === 'true';
          const allQuestions = this.querySelectorAll('.detail-explorer__faq-question');

          for (const otherQuestion of allQuestions) {
            if (otherQuestion !== question) {
              otherQuestion.setAttribute('aria-expanded', 'false');
              otherQuestion.nextElementSibling?.classList.remove('active');
            }
          }

          question.setAttribute('aria-expanded', String(!isExpanded));
          answer.classList.toggle('active', !isExpanded);
        },
        { signal }
      );
    }
  }
}

if (!customElements.get('detail-explorer')) {
  customElements.define('detail-explorer', DetailExplorerComponent);
}

document.addEventListener('shopify:section:load', (event) => {
  const target = event.target;
  if (!(target instanceof HTMLElement)) return;

  const root = target.matches('detail-explorer, [data-detail-explorer]')
    ? target
    : target.querySelector('detail-explorer, [data-detail-explorer]');

  if (root instanceof HTMLElement) {
    root.removeAttribute('data-detail-explorer-initialized');
    const tablist = root.querySelector('[role="tablist"]');
    tablist?.removeAttribute('data-de-tabs-bound');
  }
});
